import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectVideoStatus } from '../../features/displayContent/selectors'
import {
	FirstLine,
	FullScreenVideo,
	SecondLine,
	TitleWrapper,
	VideoContainer,
	VideoDetail,
	VideoFirstLine,
	VideoSecondLine,
	VideoTitleWrapper,
	Wrapper,
} from './CommonComponents'
import { DisplayableContentType } from '../../model/model'

import theme from '../../styleguide/theme'
import { selectCoreMediaRxAddons } from '../../features/coremedia/selectors'
import { ConnectRxAddonValue } from '../../model/coremedia'

const videoTransitionTime = theme.transitions.duration.shortest

const getAssetUri = (assetSrc: string | { uri?: string }): string => {
	if (typeof assetSrc === 'object') {
		return (assetSrc as { uri: string })?.uri ?? ''
	} else return assetSrc
}

const VideoWrapper: React.FC<{
	asset: ConnectRxAddonValue
	title: string
}> = ({ asset, title }) => {
	const [isLoaded, setIsLoaded] = useState(false)
	const videoStatus = useSelector(selectVideoStatus)
	const videoRef = useRef<HTMLVideoElement>(null)
	const isPlayStarted = videoStatus?.playing
	const startTime = videoStatus?.startTime
	const endTime = videoStatus?.endTime
	const isFullscreen = videoStatus?.isFullscreen
	const currentTime = videoStatus?.currentTime
	const videoAsset = asset.media.videos[0]

	useEffect(() => {
		if (!videoRef.current) {
			return
		}
		if (currentTime !== undefined) {
			videoRef.current.currentTime = currentTime
		}
		if (isPlayStarted) {
			videoRef.current.play()
		} else {
			videoRef.current.pause()
		}
	}, [isPlayStarted, currentTime])

	const loopTimeVideo = (currentTime: number) => {
		const video = videoRef.current
		if (!video) {
			return
		}
		const { duration } = video
		const shouldVideoLoop =
			(!!endTime && currentTime > endTime) ||
			(!!startTime && currentTime >= duration)

		if (shouldVideoLoop) {
			resetVideoTimeToStartTime()
		}
	}

	const resetVideoTimeToStartTime = () => {
		const video = videoRef.current!
		video.pause()
		video.currentTime = startTime || 0
		setTimeout(() => {
			video.play()
		}, 500)
	}

	const assetSrc = getAssetUri(videoAsset)

	return (
		<VideoContainer
			videoTransitionTime={videoTransitionTime}
			fullscreen={isFullscreen}
			loaded={isLoaded}
		>
			<VideoDetail data-testid={isPlayStarted ? 'playing' : 'not-playing'}>
				{isFullscreen && (
					<VideoTitleWrapper data-testid="fullscreen-detail">
						<VideoFirstLine>{title}</VideoFirstLine>
						<VideoSecondLine>{asset.value}</VideoSecondLine>
					</VideoTitleWrapper>
				)}
			</VideoDetail>
			{asset && assetSrc ? (
				<FullScreenVideo
					onTimeUpdate={ev => loopTimeVideo(ev.currentTarget.currentTime)}
					src={assetSrc}
					ref={videoRef}
					fullscreen={isFullscreen}
					isPlaying={isPlayStarted}
					muted
					onLoadStart={() => {
						setIsLoaded(false)
					}}
					onCanPlay={() => {
						setIsLoaded(true)
					}}
				/>
			) : null}
		</VideoContainer>
	)
}

const FullContent: React.FC<{
	asset: ConnectRxAddonValue
	title: string
}> = ({ asset, title }) => {
	return (
		<Wrapper data-testid="lens-type-and-coating-component">
			<TitleWrapper>
				{title && <FirstLine>{title}</FirstLine>}
				{asset && (
					<>
						<SecondLine data-testid="lens-type-and-coating-second-line">
							{asset.value}
						</SecondLine>
					</>
				)}
			</TitleWrapper>
			<VideoWrapper asset={asset} title={title} />
		</Wrapper>
	)
}

const LensTypeAndCoating: React.FC<{ type?: DisplayableContentType }> = ({
	type,
}) => {
	const [, category, detail] = (type ?? '').split(':')
	const assetKey = 'GeneralUse'
	const assets = useSelector(selectCoreMediaRxAddons)

	if (category && detail) {
		const asset = assets[assetKey].types[category].content[detail]
		const title = assets[assetKey].types[category].value
		return <FullContent asset={asset} title={title} />
	} else {
		return null
	}
}
export default LensTypeAndCoating
